<script setup>
import { computed } from 'vue';

const props = defineProps({
  user: {
    type: Object,
    required: true
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value)
  },
  showName: {
    type: Boolean,
    default: false
  }
});

const avatarUrl = computed(() => {
  if (props.user?.profile_image_url) {
    return props.user.profile_image_url;
  }

  const name = props.user?.first_name || props.user?.name || props.user?.email?.split('@')[0] || '';
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`;
});

const initials = computed(() => {
  if (props.user?.first_name) {
    return props.user.first_name.charAt(0).toUpperCase();
  }
  if (props.user?.name) {
    return props.user.name.charAt(0).toUpperCase();
  }
  if (props.user?.email) {
    return props.user.email.charAt(0).toUpperCase();
  }
  return 'U';
});

const displayName = computed(() => {
  return props.user?.first_name || props.user?.name || props.user?.email?.split('@')[0] || 'User';
});
</script>

<template>
  <div class="avatar-container">
    <div
      :class="['avatar-image', `avatar-${size}`]"
    >
      <img
        v-if="user?.profile_image_url"
        :src="avatarUrl"
        :alt="displayName"
        class="avatar-img"
        @error="$event.target.style.display = 'none'"
      >
      <span
        v-else
        class="avatar-initials"
      >
        {{ initials }}
      </span>
    </div>

    <div v-if="showName" class="avatar-name">
      <p class="avatar-display-name">
        {{ displayName }}
      </p>
      <p v-if="user?.email && user.email !== displayName" class="avatar-email">
        {{ user.email }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.avatar-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-image {
  border-radius: 50%;
  background-color: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar-xs {
  width: 24px;
  height: 24px;
  font-size: 0.75rem;
}

.avatar-sm {
  width: 32px;
  height: 32px;
  font-size: 0.875rem;
}

.avatar-md {
  width: 40px;
  height: 40px;
  font-size: 1rem;
}

.avatar-lg {
  width: 48px;
  height: 48px;
  font-size: 1.125rem;
}

.avatar-xl {
  width: 96px;
  height: 96px;
  font-size: 1.25rem;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  color: #6b7280;
  font-weight: 500;
}

.avatar-name {
  min-width: 0;
  flex: 1;
}

.avatar-display-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
}

.avatar-email {
  font-size: 0.875rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
}
</style>
